import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, of, Subject, map, switchMap, tap, throwError } from 'rxjs';
import { ConfigService } from '../config.service';
import { ApiKeyService } from './api-key.service';
import { Trip } from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { McDataMapService } from './mc-data-map.service';
import { AchTransfers, AlternatePaymentScheduleOption, ChargeTypes, NextPaymentStatus, PaymentTypes, PostTripCharge, PostTripPayment, TripChargePayment, TripFinanceChargesPayload, TripFinanceStatementDetail, TripFinanceSummary, UiTripCharge } from './types/finance';
import { Filter, Where } from '@loopback/filter';
import { Utility } from '../utility';
import { OverDuePaymentsResponse, OverduePayment } from './types/recruiting';

export const onTimeIncentivePromoCode = "ONTIME"

export interface PaymentsUpdatedArgs {
  tripId: number,
  operation: "added" | "updated" | "removed"
}

export interface TripFinanceStatementDetailUpdatedArgs {
  detail: TripFinanceStatementDetail
}
export interface AddTripChargeOrDiscountParams {
  amount: number,
  tripId: number,
  custId: number,
  total: number,
  note: string,
  type: ChargeTypes,
  perPerson: boolean,
  perPersonCount: number,
  promoCode: string

}

export interface EditTripChargeOrDiscountParams  {

  chargeDueDate: Date,
  expectedPaymentDate: Date,
  amount: number,
  perPersonAmount: number,
  perPersonCount: number,
  description: string


}

export interface DeleteTripChargeOrDiscountParams {
  paymentId: number,
  tripId: number,
  custId: number
}

export interface AddTripPaymentParams {
  custId: number,
  tripId: number,
  datePaid?: Date,
  dateReceived?: Date,
  amount?: number,
  type?: PaymentTypes,
  note?: string,
  sendReceipt?: boolean,
  checkNum?: string,
  creditExpirationDate?: Date,

}

export interface DeleteTripPaymentParams {
  paymentId: number,
  tripId: number,
  custId: number
}

export interface EditTripPaymentParams extends Omit<PostTripPayment, "sendReceipt"> {
  
}


@Injectable({
  providedIn: 'root'
})
export class TripFinanceService {


  private paymentsUpdated = new Subject<PaymentsUpdatedArgs>()
  paymentsUpdated$ = this.paymentsUpdated.asObservable()

  private tripFinanceStatementDetailsUpdated = new Subject<TripFinanceStatementDetailUpdatedArgs>()
  tripFinanceStatementDetailsUpdated$ = this.tripFinanceStatementDetailsUpdated.asObservable()

  private readonly tripChargePaymentDateFields: DateField<TripChargePayment>[] = [
    { dateType: "central", fieldName: "amountPaidDate" },
    { dateType: "central", fieldName: "paymentReceivedDate" }
  ]

  private readonly financeSummaryDateFields: DateField<TripFinanceSummary>[] = [
    { dateType: "central", fieldName: "registrationDue" },
    { dateType: "central", fieldName: "firstPaymentDue" },
    { dateType: "central", fieldName: "finalPaymentDue" },
    { dateType: "central", fieldName: "dateReserved" },
    { dateType: "central", fieldName: "tripDate" },
    { dateType: "utc", fieldName: "firstNoticePrepared" },
    { dateType: "utc", fieldName: "secondNoticePrepared" }
  ]

  private readonly financeStatementDetailsDateFields: DateField<TripFinanceStatementDetail>[] = [
    { dateType: "utc", fieldName: "lastSent" },
    { dateType: "utc", fieldName: "firstNoticePrepared" },
    { dateType: "utc", fieldName: "secondNoticePrepared" }
  ]

  private readonly tripChargeDateFields: DateField<UiTripCharge>[] = [
    { dateType: 'local', fieldName: "chargeDueDate" },
    { dateType: "local", fieldName: "dateCreated" },
    { dateType: "local", fieldName: "expectedPaymentDate"}
  ]

  private readonly overduePaymentDateFields: DateField<OverduePayment>[] = [
    { dateType: 'local', fieldName: "chargeDueDate" }
  ]

  constructor(
    private http: HttpClient,
    private config: ConfigService,
    private mcDataMapService: McDataMapService,
    private apiKeyService: ApiKeyService,
  ) { }


  emptyNextPaymentStatus(): NextPaymentStatus {
    return {
      hasNextPayment: false,
      pastDueAmount: 0,
    };
  }

  emptyTripFinanceStatementDetail(): TripFinanceStatementDetail {
    return {
      id: 0,
      disableEmail: 0,
      lastSent: new Date(1000, 0, 1),
      tripId: 0,
      firstNoticePrepared: new Date(1000, 0, 1),
      secondNoticePrepared: new Date(1000, 0, 1)

    }
  }

  private readonly nextPaymentDueDateFields: DateField<Extract<NextPaymentStatus, { hasNextPayment: true }>>[] = [
    { dateType: 'central', fieldName: 'dueDate' },
  ];

  getNextPaymentDetails(tripId: number) {
    return this.http.get<NextPaymentStatus>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/next-payment`).pipe(
      map(payment => {
        if (payment.hasNextPayment) {
          return DataTransform.replaceDatesDirect(payment, this.nextPaymentDueDateFields);
        }
        return payment;
      }),
    )
  }

  private readonly tripFinanceChargesPayloadDateFields: DateField<TripFinanceChargesPayload>[] = [
    { dateType: 'local', fieldName: "dueDateLimit" },
  ]

  getTripCharges(tripId: number) {
    return this.http.get<TripFinanceChargesPayload>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/charges`).pipe(
      switchMap(payload => {
        return DataTransform.replaceDatesArray<UiTripCharge>(payload.charges, this.tripChargeDateFields).pipe(
          map(charges => DataTransform.replaceDatesDirect(
            { ...payload, charges },
            this.tripFinanceChargesPayloadDateFields,
          ))
        )
      })
    )
  }

  getTripChargesOnly(tripId: number) {
    return this.getTripCharges(tripId).pipe(map(payload => payload.charges))
  }

  getAlternatePaymentSchedules() {
    return this.http.get<AlternatePaymentScheduleOption[]>(`${this.config.dataApiUrl}/trips/alternate-payment-schedules`)
  }

  applyAlternatePaymentSchedule(tripId: number, scheduleKey: string | null) {
    return this.http.post<void>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/alternate-payment-schedule`, { scheduleKey }).pipe(
      tap(() => {
        this.paymentsUpdated.next({ tripId, operation: "updated" })
      }),
    )
  }


  addTripChargeOrDiscount(tripId: number, params: PostTripCharge) {
    return this.http.post(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/charges`, params).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "added" })
      })
    )
  }

  updateTripChargeOrDiscount(tripId: number, tripChargeId: number, params: Partial<EditTripChargeOrDiscountParams>) {
    return this.http.patch(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/charges/${tripChargeId.toString()}`, params).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "updated" })
      })
    )
  }


  deleteTripChargeOrDiscount(tripId: number, tripChargeId: number) {
    return this.http.delete(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/charges/${tripChargeId.toString()}`).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "removed" })
      })
    )
  }

  getTripPayments(tripId: number) {
    return this.http.get<TripChargePayment[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/payments`).pipe(
      switchMap(payments => {
        return DataTransform.replaceDatesArray(payments, this.tripChargePaymentDateFields)
      })
    )
  }


  addTripPayment(tripId: number,params: PostTripPayment) {
    return this.http.post(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/payments`, params).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "added" })
      })
    )
  }


  editTripPayment(tripId: number, paymentId: number, params: Partial<EditTripPaymentParams>) {

    
    if (params.paymentType === PaymentTypes.TransferToWallet && params.amountPaid && params.amountPaid > 0) {
      params.amountPaid = params.amountPaid * -1
    }

    if (!params.paymentReceivedDate) {
      throw new Error("Must specify date received")
    }

    return this.http.patch(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/payments/${paymentId.toString()}`, params).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "updated" })
      })
    )
  }
  

  deleteTripPayment(tripId: number, paymentId: number) {
    return this.http.delete(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/payments/${paymentId.toString()}`).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "removed" })
      })
    )
  }

  getAchTransfers(tripId: number) {
    return this.http.get<AchTransfers[]>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/ach-transfers`).pipe(
      switchMap(transfers => {
        return DataTransform.replaceDatesArray(transfers, [{ dateType: "central", fieldName: "created" }])
      })
    )
  }


  getInvoicePdf(tripId: number) {
    return this.http.get(`${this.config.dataApiUrl}/finance/invoice/${tripId.toString()}`, { responseType: "blob" })
  }

  getFirstPaymentLettersLink(tripIds: number[]) {
    return `${this.config.mcLegacyApi}/upgrade/finance/form-letters/firstNotice.cfm?tripIds=${tripIds.join(",")}`
  }

  getSecondPaymentLettersLink(tripIds: number[]) {
    return `${this.config.mcLegacyApi}/upgrade/finance/form-letters/secondNotice.cfm?tripIds=${tripIds.join(",")}`
  }

  downloadFirstPaymentLetters(tripIds: number[], preview: boolean) {
   
    const postBody = {
      tripIds: tripIds.join(","),
      pdfOnly: preview
    }
   
    return this.http.post(`${this.config.mcLegacyApi}/upgrade/finance/form-letters/firstNotice.cfm`, this.mcDataMapService.convertToFormData(postBody), {observe: "response", responseType: "blob"}).pipe(
      switchMap(resp => {
        let contentHeader = resp.headers.get("Content-Disposition")
       
          const filename = Utility.getContentDispositionFilename(contentHeader ?? "")

          return of({filename: filename, blob: resp.body})

      })
    )
  }

  downloadSecondPaymentLetters(tripIds: number[], preview: boolean) {
    
    const postBody = {
      tripIds: tripIds.join(","),
      pdfOnly: preview
    }
    
    return this.http.post(`${this.config.mcLegacyApi}/upgrade/finance/form-letters/secondNotice.cfm`, this.mcDataMapService.convertToFormData(postBody), {observe: "response", responseType: "blob"}).pipe(
      switchMap(resp => {
        let contentHeader = resp.headers.get("Content-Disposition")
       
          const filename = Utility.getContentDispositionFilename(contentHeader ?? "")

          return of({filename: filename, blob: resp.body})

      })
    )
  }

  emailInvoiceToTeam(params: Pick<Trip, "id" | "custId">) {
    return this.http.post(`${this.config.dataApiUrl}/finance/invoice/email/${params.id}`, {})
  }


  // getPaymentReceiptHtml(paymentId: number): Observable<string> {

  //   return this.apiKeyService.getLegacyMcStaffKey().pipe(
  //     switchMap(apiKey => {
  //       const data = {
  //         sendEmail: false,
  //         apiKey: apiKey.apiKey
  //       }
  //       return this.http.post<string>(`${this.config.mcLegacyApi}/mcapi/finance/trip-payment-receipt/${paymentId.toString()}`, data, { responseType: "json" })
  //     })
  //   )

  // }


  // }

  emailReceiptToGLandFinance(paymentId: number) {
    return this.http.post(`${this.config.dataApiUrl}/finance/payments/receipt/email/${paymentId.toString()}`, {})
  }

  downloadReceipt(paymentId: number) {
    return this.http.get(`${this.config.dataApiUrl}/finance/payments/receipt/${paymentId.toString()}`, { responseType: "blob" }).pipe(
      switchMap(blob => {
        return of({ filename: `Receipt-${paymentId}.pdf`, blob: blob })
      })
    )
  }

  getPaymentsDueSummary(tripStartDate: Date, pastDuePayments: ChargeTypes[]): Observable<TripFinanceSummary[]> {
    const nowConverted = DateTime.now().setZone("utc", { keepLocalTime: true }).toJSDate()
    const startDateConverted = DateTime.fromJSDate(tripStartDate).setZone("utc", { keepLocalTime: true }).toJSDate()
    let or: Where<TripFinanceSummary>[] = []


    if (pastDuePayments.includes(ChargeTypes.Registration)) {
      or = [...or, { registrationDue: { lt: nowConverted }, isRegistrationPaid: 0 }]
    }

    if (pastDuePayments.includes(ChargeTypes.First_Payment)) {
      or = [...or, { firstPaymentDue: { lt: nowConverted }, isFirstPaymentPaid: 0 }]
    }

    if (pastDuePayments.includes(ChargeTypes.Final_Payment)) {
      or = [...or, { finalPaymentDue: { lt: nowConverted }, amountRemaining: { gt: 0 } }]
    }


    const filter: Filter<TripFinanceSummary> = {

      where: {
        or: or,
        tripDate: { gte: startDateConverted },
        custId: {neq: 149},
        alternatePaymentSchedule: { eq: null },
      }
    }

    return this.http.get<TripFinanceSummary[]>(`${this.config.dataApiUrl}/trips/finance/summary`, { params: { filter: JSON.stringify(filter) } }).pipe(
      switchMap(details => {
        return DataTransform.replaceDatesArray(details, this.financeSummaryDateFields)
      })
    )
  }

  getFinanceStatementDetails(tripId: number): Observable<TripFinanceStatementDetail> {
    return this.http.get<TripFinanceStatementDetail>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/finance-statement-details`).pipe(
      switchMap(detail => {
        return DataTransform.replaceDates(detail, this.financeStatementDetailsDateFields)
      })
    )
  }

  updateFinanceStatementDetails(tripId: number, data: Partial<Omit<TripFinanceStatementDetail, "id">>): Observable<TripFinanceStatementDetail> {

    return this.http.patch<TripFinanceStatementDetail>(`${this.config.dataApiUrl}/trips/${tripId.toString()}/finance/finance-statement-details`, data).pipe(
      switchMap(detail => {
        return DataTransform.replaceDates(detail, this.financeStatementDetailsDateFields)
      }),
      tap(detail => {
        this.tripFinanceStatementDetailsUpdated.next({ detail: detail })
      })
    )

  }

  getOverdueRegiFeesForStaff(staffId: number) {
    return this.http.get<OverDuePaymentsResponse>(`${this.config.dataApiUrl}/trip-finance/overdue-regi-fees//${staffId}`);
  }

  getOverduePaymentsForStaff(staffId: number) {
    return this.http.get<OverDuePaymentsResponse>(`${this.config.dataApiUrl}/trip-finance/overdue-payments/${staffId}`).pipe(
      switchMap(response => {
        return of({
          ...response,
          overduePayments: DataTransform.replaceDatesArrayDirect(response.overduePayments, this.overduePaymentDateFields)
        })
      })
    );
  }

  updatePaymentSchedule(tripId: number) {
    return this.http.post(`${this.config.dataApiUrl}/trips/${tripId.toString()}/update-payment-schedule`, {}).pipe(
      tap(result => {
        this.paymentsUpdated.next({ tripId: tripId, operation: "updated" })
      })
    )
  }
  
 }
